import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { SectionEmptyState } from "../../shell/SectionScreen";
import {
  CHAT_SEARCH_NO_MATCHES_BODY,
  chatSearchNoMatchesHeadline,
} from "./chatListActionsCopy";
import { ChatListRow } from "./ChatListRow";
import type { typeMessagesListState } from "./messagesListState";
import { PendingInvitationRow } from "./PendingInvitationRow";
import { RequestsRow } from "./RequestsRow";

export function MessagesListBody({
  state,
  onSelectRequests,
  onSelectInvitation,
  onSelectChat,
  onAcceptInvitation,
  onDeclineInvitation,
  onRetry,
  onOpenChatActions,
}: {
  state: typeMessagesListState;
  onSelectRequests: () => void;
  onSelectInvitation: (chatInvitationId: string) => void;
  onSelectChat: (chatId: string) => void;
  onAcceptInvitation: (chatInvitationId: string) => void;
  onDeclineInvitation: (chatInvitationId: string) => void;
  onRetry: () => void;
  /** ISSUE-1362. Opens the quick actions for one chat row. */
  onOpenChatActions?: (chatId: string) => void;
}): ReactNode {
  if (state.phase === "loading") {
    return (
      <div className="flex flex-col gap-3" data-testid="messages-loading">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (state.phase === "failed") {
    return (
      <SectionEmptyState
        headline="We could not load your messages"
        body="Check your connection and try again."
        action={{ label: "Try again", onAction: onRetry }}
      />
    );
  }

  if (state.phase === "empty") {
    return (
      <SectionEmptyState
        headline="No chats yet"
        body="Join a pool or start a chat."
      />
    );
  }

  const hasNoMatches =
    state.searchText !== "" &&
    state.chatRows.length === 0 &&
    state.invitationRows.length === 0;

  return (
    <>
      {hasNoMatches ? (
        <div
          className="flex flex-col items-center gap-1 py-10 text-center"
          data-testid="messages-search-no-matches"
        >
          <p className="text-body-strong text-ink">
            {chatSearchNoMatchesHeadline(state.searchText)}
          </p>
          <p className="text-body text-ink-secondary">
            {CHAT_SEARCH_NO_MATCHES_BODY}
          </p>
        </div>
      ) : null}
      {state.showRequestsRow ? (
        <RequestsRow
          onSelect={onSelectRequests}
          testId="messages-requests-row"
        />
      ) : null}
      {state.invitationRows.map((row) => (
        <PendingInvitationRow
          key={row.chatInvitationId}
          row={row}
          onSelect={() => {
            onSelectInvitation(row.chatInvitationId);
          }}
          onAccept={() => {
            onAcceptInvitation(row.chatInvitationId);
          }}
          onDecline={() => {
            onDeclineInvitation(row.chatInvitationId);
          }}
          testId="messages-invitation-row"
        />
      ))}
      {state.chatRows.map((row) => (
        <ChatListRow
          key={row.chatId}
          row={row}
          photoUrl={row.avatarUrl}
          onSelect={() => {
            onSelectChat(row.chatId);
          }}
          {...(onOpenChatActions !== undefined
            ? {
                onOpenActions: () => {
                  onOpenChatActions(row.chatId);
                },
              }
            : {})}
          testId={`chat-row-${row.chatId}`}
        />
      ))}
    </>
  );
}
